import React, {useRef, useState, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import Select from "react-select";
import {getAddress, cleanupAddresses} from "../../../redux/actions";
import styles from "./AddressInput.module.scss";
import Dropdown from "../dropdown/Dropdown";
import Loading from '../loading/Loading';
import {faInfoCircle, faTimes} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

const AddressInput = props => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(cleanupAddresses());
  }, []);

  const addressData = useSelector(state => state.address);

  const customType = props.address && props.address.customType;
  const [streetAddress, setStreetAddress] = useState(customType ?
    props.address.value.details.addressLine1 : null);
  const [suburb, setSuburb] = useState(customType ?
    props.address.value.details.suburb : null);
  const allStates = [
    {
      value: 'NSW',
      label: 'NSW'
    },
    {
      value: 'QLD',
      label: 'QLD'
    },
    {
      value: 'SA',
      label: 'SA'
    },
    {
      value: 'TAS',
      label: 'TAS'
    },
    {
      value: 'VIC',
      label: 'VIC'
    },
    {
      value: 'WA',
      label: 'WA'
    },
    {
      value: 'ACT',
      label: 'ACT'
    },
    {
      value: 'NT',
      label: 'NT'
    }
  ];
  const iniState = {
    value: null,
    label: 'State'
  };
  const [state, setState] = useState(customType ?
    allStates.find(s2 => s2.value === props.address.value.details.state) : iniState);
  const [postcode, setPostcode] = useState(customType ?
    props.address.value.details.postcode : null);

  const [customAddress, setCustomAddress] = useState(customType);
  const [currentAddress, setCurrentAddress] = useState(props.address);
  props.setValidationResult(props.address !== null)
  const [currentAddressLastTimestamp, setCurrentAddressLastTimestamp] = useState(null);
  const [timerRunning, setTimerRunning] = useState(false);
  const addressRef = useRef(currentAddress);
  addressRef.current = currentAddress;
  const currentAddressLastTimestampRef = useRef(currentAddressLastTimestamp);
  currentAddressLastTimestampRef.current = currentAddressLastTimestamp;
  const timerRunningRef = useRef(timerRunning);
  timerRunningRef.current = timerRunning;

  const resetTimeout = (initial) => {
    const waitSecs = 1;
    const currentTimestamp = Date.now();
    const tsDiff = currentAddressLastTimestampRef.current ? currentTimestamp - currentAddressLastTimestampRef.current : 0;
    if (tsDiff < (500 * waitSecs)) {
      if (timerRunningRef.current || initial) {
        setTimeout(resetTimeout, 100);
      }
    } else {
      retrieveNormalizedAddress();
    }
  }

  const retrieveNormalizedAddress = () => {
    setTimerRunning(false);
    setCurrentAddressLastTimestamp(null);
    if (addressRef.current) {
      dispatch(getAddress(addressRef.current.label));
    }
  };

  const handleSetCurrentAddress = (address, action) => {
    if (action.action !== 'input-change') {
      return;
    }
    setCurrentAddressLastTimestamp(Date.now());
    setCurrentAddress({label: address});
    if (!timerRunningRef.current) {
      setTimerRunning(true);
      resetTimeout(true);
    }
  }

  const handleSelectAddress = (selectedAddress) => {
    const isManual = selectedAddress.label.indexOf("Use address as entered:") !== -1;
    if (!isManual) {
      setCurrentAddressLastTimestamp(Date.now());
      setCurrentAddress(selectedAddress);
      props.setAddress({
        customType: false,
        ...selectedAddress
      });
      props.setValidationResult(true);
    } else {
      setCustomAddress(true);
      setStreetAddress(selectedAddress.value.details.addressLine1);
      setSuburb(selectedAddress.value.details.suburb);
      setState(selectedAddress.value.details.state ? allStates.find(s2 => s2.value === selectedAddress.value.details.state) : iniState);
      setPostcode(selectedAddress.value.details.postcode);
      validateCustomAddress();
    }
  }

  const optionsForAddress = addressData && addressData.results ?
    addressData.results
      .map(ad => {
        return {
          value: ad,
          label: ad.label
        };
      }) : [];

  const validateCustomAddress = () => {
    if (props.setValidationResult) {
      props.setValidationResult(streetAddress !== null && streetAddress.length > 2 && suburb !== null && suburb.length > 2 &&
        state && state.value !== null && postcode !== null && postcode.length > 2);
    }
  }

  useEffect(() => {
    validateCustomAddress();
    if (streetAddress && suburb && state && postcode) {
      const label = `${streetAddress}, ${suburb ? suburb.toUpperCase() : ''} ${state && state.value ? state.value.toUpperCase() : ''} ${postcode}`;
      const address = {
        customType: true,
        value: {
          label,
          details: {
            addressLine1: streetAddress,
            suburb: suburb ? suburb.toUpperCase() : '',
            state: state && state.value ? state.value.toUpperCase() : '',
            postcode: postcode,
            partialAddress: label
          }
        },
        label
      }
      props.setAddress(address);
    }
  }, [state, postcode, suburb, streetAddress]);

  return <div className={styles.addressSelector}>
    <div className={'row'}>
      <div className={`col-7`}>
        {props.title}
      </div>
      <div className={`col-5 ${styles.switchContainer}`}>
        <a
          className={styles.switchLink}
          onClick={() => {
            props.setValidationResult(false);
            props.setAddress(null);
            setCurrentAddress(null);
            setStreetAddress(null);
            setSuburb(null);
            setState(iniState);
            setPostcode(null);
            setCustomAddress(!customAddress);
          }}
        >
          {customAddress ? 'Address lookup' : 'Enter manually'}
        </a>
      </div>
    </div>
    {customAddress ? <div className={'row'}>
        <div className={`col-12 col-md-5 ${styles.addressLineMobile}`}>
          <input
            className={`${styles.inputBox}`}
            placeholder={'Postal Street Address'}
            value={streetAddress}
            onChange={(event) => {
              props.onChange();
              setStreetAddress(event.target.value);
              validateCustomAddress();
            }}
          />
        </div>
        <div className={`col-6 col-md-3 ${styles.addressLineMobile}`}>
          <input
            className={`${styles.inputBox}`}
            placeholder={'Suburb'}
            value={suburb}
            onChange={(event) => {
              props.onChange();
              setSuburb(event.target.value);
              validateCustomAddress();
            }}
          />
        </div>
        <div className={`col-3 col-md-2 ${styles.addressLineMobile}`}>
          <Dropdown
            options={allStates}
            className={styles.dropdown}
            value={state}
            onSelect={(val) => {
              props.onChange();
              setState(val);
              validateCustomAddress();
            }}
          />
        </div>
        <div className={`col-3 col-md-2 ${styles.addressLineMobile}`}>
          <input
            className={`${styles.inputBox}`}
            placeholder={'Postcode'}
            value={postcode}
            onChange={(event) => {
              props.onChange();
              setPostcode(event.target.value);
              validateCustomAddress();
            }}
          />
        </div>
      </div> :
      [addressData.loading ? <Loading className={styles.loadingAddresses} label={false} type={'ajax'}/> : null,
        props.address ? <FontAwesomeIcon
          onClick={() => {
            dispatch(cleanupAddresses());
            props.setAddress(null);
          }}
          icon={faTimes}
          size="lg"
          className={styles.removeAddress}
        /> : null,
        <Select
          value={props.address}
          onInputChange={handleSetCurrentAddress}
          onChange={handleSelectAddress}
          classNamePrefix={`addressSelect`}
          options={optionsForAddress}
          placeholder={'Postal Street Address'}
        />]}
  </div>
}

export default AddressInput;
